<template>
  <v-card :loading="busy">
    <v-card-title>
      {{ room.name }}
      <v-spacer></v-spacer>
      <v-switch
          :input-value="room.on"
          inset
          hide-details
          class="mt-0 pt-0"
          color="orange"
          :disabled="busy"
          @change="setOn"
      ></v-switch>
      <v-menu offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon v-bind="attrs" v-on="on">
            <v-icon>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item @click="openEdit">
            <v-list-item-icon><v-icon>mdi-pencil</v-icon></v-list-item-icon>
            <v-list-item-title>Bearbeiten</v-list-item-title>
          </v-list-item>
          <v-list-item @click="remove">
            <v-list-item-icon><v-icon>mdi-delete</v-icon></v-list-item-icon>
            <v-list-item-title>Löschen</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-card-title>

    <v-card-text align="left">
      <!-- Farbpalette für die Lampen des Raums -->
      <div v-if="hasLamps" class="mb-3">
        <v-avatar
            v-for="preset in presets"
            :key="preset.label"
            :color="hex(preset.rgb)"
            size="26"
            class="mr-2 preset"
            :title="preset.label"
            @click="setColor(preset.rgb)"
        ></v-avatar>
      </div>

      <v-chip
          v-for="device in room.devices"
          :key="device.id"
          small
          class="mr-1 mb-1"
          :color="device.on ? 'orange' : ''"
      >
        <v-icon small left>{{ icon(device) }}</v-icon>
        {{ device.display_name }}
      </v-chip>
      <span v-if="room.devices.length === 0" class="text--secondary">
        Keine Geräte zugeordnet
      </span>
    </v-card-text>

    <!-- Bearbeiten-Dialog -->
    <v-dialog v-model="editOpen" max-width="500">
      <v-card>
        <v-card-title>Raum bearbeiten</v-card-title>
        <v-card-text>
          <v-text-field v-model="editName" label="Name"></v-text-field>
          <v-select
              v-model="editDeviceIds"
              :items="deviceItems"
              label="Geräte"
              multiple
              chips
              deletable-chips
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="editOpen = false">Abbrechen</v-btn>
          <v-btn color="primary" text :disabled="!editName" @click="saveEdit">Speichern</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { smarthausApi, COLOR_PRESETS, CATEGORY_ICONS, rgbToHex } from '../../../services/smarthaus'

export default {
  name: 'RoomCard',
  props: {
    room: { type: Object, required: true },
    // Alle bekannten Geräte (für die Zuordnung im Bearbeiten-Dialog).
    allDevices: { type: Array, default: () => [] },
  },
  data: () => ({
    busy: false,
    editOpen: false,
    editName: '',
    editDeviceIds: [],
    presets: COLOR_PRESETS,
  }),
  computed: {
    hasLamps() {
      return this.room.devices.some((d) => d.category === 'lamp')
    },
    deviceItems() {
      return this.allDevices
          .filter((d) => d.category === 'lamp' || d.category === 'plug')
          .map((d) => ({ text: d.display_name, value: d.id }))
    },
  },
  methods: {
    hex(rgb) { return rgbToHex(rgb[0], rgb[1], rgb[2]) },
    icon(device) { return CATEGORY_ICONS[device.category] || CATEGORY_ICONS.other },
    async run(promise) {
      this.busy = true
      try {
        const data = await promise
        this.$emit('changed', data && data.room ? data.room : null)
      } catch (e) {
        console.error('[RoomCard]', e)
        this.$emit('changed', null)
      } finally {
        this.busy = false
      }
    },
    setOn(on) { return this.run(smarthausApi.roomOn(this.room.id, !!on)) },
    setColor(rgb) { return this.run(smarthausApi.roomColor(this.room.id, rgb[0], rgb[1], rgb[2])) },
    openEdit() {
      this.editName = this.room.name
      this.editDeviceIds = [...this.room.device_ids]
      this.editOpen = true
    },
    saveEdit() {
      this.editOpen = false
      return this.run(smarthausApi.roomUpdate(this.room.id, {
        name: this.editName,
        device_ids: this.editDeviceIds,
      }))
    },
    remove() {
      if (!confirm(`Raum "${this.room.name}" löschen?`)) return
      return this.run(smarthausApi.roomDelete(this.room.id))
    },
  },
}
</script>

<style scoped>
.preset {
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.3) !important;
}
</style>
